import apiClient from '../shared/apiClient';
import { 
  orgUnitsByClientAndLevel,
  locationByClientId,
  orgUnitEmployeesByOrgUnit
} from 'graphql/queries';

/**
 * Service for managing organizational units
 * Copied from locationAdminService.js with minimal changes
 */
class OrgUnitService {
  // Get minimal org unit data for master list (optimized)
  async getOrgUnitsMaster(clientId) {
    try {
      const startTime = performance.now();
      
      const orgUnitsResult = await apiClient.query(orgUnitsByClientAndLevel, { 
        clientId,
        limit: 500  // Org units are typically fewer than locations
      });
      
      const orgUnits = orgUnitsResult?.data?.orgUnitsByClientAndLevel?.items || [];
      
      // Return minimal fields only
      const masterList = orgUnits.map(unit => ({
        id: unit.id,
        name: unit.name,
        orgLevel: unit.orgLevel,  // Keep orgLevel property name consistent
        level: unit.orgLevel,     // Also include as 'level' for backward compatibility
        isActive: unit.isActive !== false,
        parentOrgUnitId: unit.parentOrgUnitId || null
      }));
      
      const fetchTime = performance.now() - startTime;

      return masterList;
    } catch (error) {
      console.error('Error fetching org units master:', error);
      throw error;
    }
  }

  // Get org units grouped by level with location counts
  async getOrgUnitsGroupedByLevel(clientId) {
    try {
      // Fetch all org units
      const orgUnitsResult = await apiClient.query(orgUnitsByClientAndLevel, { clientId });
      const orgUnits = orgUnitsResult?.data?.orgUnitsByClientAndLevel?.items || [];

      // Fetch all locations to calculate location counts
      const locationsResult = await apiClient.query(locationByClientId, { clientId });
      const locations = locationsResult?.data?.locationByClientId?.items || [];

      // Fetch manager assignments for each org unit using OrgUnitEmployee
      const managersByUnit = {};
      
      // Batch fetch manager assignments for all org units
      const managerPromises = orgUnits.map(async (unit) => {
        try {
          const assignmentResult = await apiClient.query(orgUnitEmployeesByOrgUnit, {
            orgUnitId: unit.id,
            filter: { 
              isActive: { eq: true },
              clientId: { eq: clientId }
            },
            limit: 10 // Get more in case there are duplicates
          });
          const assignments = assignmentResult?.data?.orgUnitEmployeesByOrgUnit?.items || [];
          
          // If multiple active assignments, use the most recent one
          let activeAssignment = null;
          if (assignments.length > 0) {
            if (assignments.length > 1) {
              console.warn(`Multiple active assignments found for unit ${unit.id}:`, assignments.length);
              // Sort by effectiveDate or createdAt to get the most recent
              assignments.sort((a, b) => {
                const dateA = new Date(a.effectiveDate || a.createdAt);
                const dateB = new Date(b.effectiveDate || b.createdAt);
                return dateB - dateA; // Most recent first
              });
            }
            activeAssignment = assignments[0];
          }
          if (activeAssignment) {
            managersByUnit[unit.id] = {
              employeeId: activeAssignment.employeeId,
              managerName: activeAssignment.employeeNameSnapshot,
              effectiveDate: activeAssignment.effectiveDate
            };
          }
        } catch (error) {
          // No manager found, that's ok
        }
      });
      
      await Promise.all(managerPromises);

      // Build a hierarchy map for efficient traversal
      const unitById = {};
      const childrenByParentId = {};
      
      orgUnits.forEach(unit => {
        unitById[unit.id] = unit;
        if (unit.parentId) {
          if (!childrenByParentId[unit.parentId]) {
            childrenByParentId[unit.parentId] = [];
          }
          childrenByParentId[unit.parentId].push(unit.id);
        }
      });

      // Function to get all descendant unit IDs (including self)
      const getDescendantIds = (unitId) => {
        const descendants = [unitId];
        const children = childrenByParentId[unitId] || [];
        children.forEach(childId => {
          descendants.push(...getDescendantIds(childId));
        });
        return descendants;
      };

      // Calculate location count for each unit (including children)
      const locationCountByUnit = {};
      orgUnits.forEach(unit => {
        const descendantIds = getDescendantIds(unit.id);
        const unitLocations = locations.filter(loc => {
          // Check if location is assigned to this unit or any of its descendants
          return descendantIds.includes(loc.orgUnitId) ||
                 descendantIds.includes(loc.orgLevel_1) ||
                 descendantIds.includes(loc.orgLevel_2) ||
                 descendantIds.includes(loc.orgLevel_3);
        });
        locationCountByUnit[unit.id] = unitLocations.length;
      });

      // Group units by org level
      const unitsByLevel = {};
      
      orgUnits.forEach(unit => {
        if (!unitsByLevel[unit.orgLevel]) {
          unitsByLevel[unit.orgLevel] = [];
        }
        
        const managerInfo = managersByUnit[unit.id];
        unitsByLevel[unit.orgLevel].push({
          id: unit.id,
          name: unit.name,
          locationCount: locationCountByUnit[unit.id] || 0,
          hasManager: !!managerInfo,
          managerUserId: managerInfo?.userId || unit.managerUserId,
          managerName: managerInfo?.managerName,
          isActive: unit.isActive !== false,
          parentId: unit.parentId,
          orgLevel: unit.orgLevel
        });
      });

      // Sort units within each level by name
      Object.keys(unitsByLevel).forEach(level => {
        unitsByLevel[level].sort((a, b) => a.name.localeCompare(b.name));
      });

      return unitsByLevel;
    } catch (error) {
      console.error('❌ Error fetching org units grouped by level:', error);
      throw error;
    }
  }

  // Legacy method - kept for backward compatibility but redirects to new method
  async getRegionalOverview(clientId) {
    try {
      const unitsByLevel = await this.getOrgUnitsGroupedByLevel(clientId);
      
      // Transform the data to match the legacy format
      // Legacy format was focused on ORGLEVEL_3 (regions) only
      const regionData = unitsByLevel['ORGLEVEL_3'] || [];
      
      return {
        regions: regionData.map(region => ({
          ...region,
          areas: [], // Legacy format included nested areas
          districts: [] // Legacy format included nested districts
        }))
      };
    } catch (error) {
      console.error('Error fetching regional overview:', error);
      throw error;
    }
  }

  // Get active org units in hierarchical structure for Units tab
  async getActiveOrgUnitsHierarchy(clientId) {
    try {
      // Fetch all org units for the client using the correct query
      const orgUnitsResult = await apiClient.query(orgUnitsByClientAndLevel, { clientId });
      
      const allOrgUnits = orgUnitsResult?.data?.orgUnitsByClientAndLevel?.items || [];
      // Filter for active units only
      const orgUnits = allOrgUnits.filter(unit => unit.isActive !== false);

      // Fetch manager assignments for each org unit using OrgUnitEmployee
      const managersByUnit = {};
      const managerPromises = orgUnits.map(async (unit) => {
        try {
          const assignmentResult = await apiClient.query(orgUnitEmployeesByOrgUnit, {
            orgUnitId: unit.id,
            filter: { 
              isActive: { eq: true },
              clientId: { eq: clientId }
            },
            limit: 10 // Get more in case there are duplicates
          });
          const assignments = assignmentResult?.data?.orgUnitEmployeesByOrgUnit?.items || [];
          
          // If multiple active assignments, use the most recent one
          let activeAssignment = null;
          if (assignments.length > 0) {
            if (assignments.length > 1) {
              console.warn(`Multiple active assignments found for unit ${unit.id}:`, assignments.length);
              // Sort by effectiveDate or createdAt to get the most recent
              assignments.sort((a, b) => {
                const dateA = new Date(a.effectiveDate || a.createdAt);
                const dateB = new Date(b.effectiveDate || b.createdAt);
                return dateB - dateA; // Most recent first
              });
            }
            activeAssignment = assignments[0];
          }
          if (activeAssignment) {
            managersByUnit[unit.id] = {
              managerId: activeAssignment.employeeId,
              managerName: activeAssignment.employeeNameSnapshot || activeAssignment.employeeNameSnapshot
            };
          }
        } catch (error) {
          // No manager found, that's ok
        }
      });
      
      await Promise.all(managerPromises);

      // Get locations to count per org unit
      const locationsResult = await apiClient.query(locationByClientId, { 
        clientId,
        limit: 1000 
      });
      const locations = locationsResult?.data?.locationByClientId?.items || [];
      
      // Count locations per org unit
      const locationCounts = {};
      locations.forEach(loc => {
        // Count for each org level
        if (loc.orgLevel_1) {
          locationCounts[loc.orgLevel_1] = (locationCounts[loc.orgLevel_1] || 0) + 1;
        }
        if (loc.orgLevel_2) {
          locationCounts[loc.orgLevel_2] = (locationCounts[loc.orgLevel_2] || 0) + 1;
        }
        if (loc.orgLevel_3) {
          locationCounts[loc.orgLevel_3] = (locationCounts[loc.orgLevel_3] || 0) + 1;
        }
      });

      // Build hierarchical structure
      const unitMap = {};
      const rootUnits = [];

      // First pass: create all units
      orgUnits.forEach(unit => {
        const managerInfo = managersByUnit[unit.id];
        unitMap[unit.id] = {
          id: unit.id,
          name: unit.name,
          levelType: this.getLevelTypeLabel(unit.orgLevel),
          orgLevel: this.getOrgLevelNumber(unit.orgLevel),
          manager: managerInfo?.managerName || unit.managerName || null,
          managerId: managerInfo?.managerId || unit.managerUserId || null,
          locationCount: locationCounts[unit.id] || 0,
          parentId: unit.parentOrgUnitId || unit.parentId || null,
          isActive: unit.isActive !== false,
          children: []
        };
      });

      // Second pass: build hierarchy
      orgUnits.forEach(unit => {
        const parentId = unit.parentOrgUnitId || unit.parentId;
        if (parentId && unitMap[parentId]) {
          unitMap[parentId].children.push(unitMap[unit.id]);
        } else {
          // No parent = root level unit
          rootUnits.push(unitMap[unit.id]);
        }
      });

      // Sort units at each level
      const sortUnits = (units) => {
        units.sort((a, b) => a.name.localeCompare(b.name));
        units.forEach(unit => {
          if (unit.children && unit.children.length > 0) {
            sortUnits(unit.children);
          }
        });
      };
      sortUnits(rootUnits);

      return rootUnits;
    } catch (error) {
      console.error('❌ Error fetching active org units hierarchy:', error);
      return [];
    }
  }

  // Get inactive org units from history (those with isActive = false)
  async getInactiveOrgUnitsHistory(clientId) {
    try {
      // Fetch all org units for the client
      const orgUnitsResult = await apiClient.query(orgUnitsByClientAndLevel, { clientId });
      
      const allOrgUnits = orgUnitsResult?.data?.orgUnitsByClientAndLevel?.items || [];
      // Filter for inactive units only
      const inactiveUnits = allOrgUnits.filter(unit => unit.isActive === false);

      // Fetch manager assignments for inactive units using OrgUnitEmployee
      // For inactive units, we want the last active assignment before deactivation
      const managersByUnit = {};
      const managerPromises = inactiveUnits.map(async (unit) => {
        try {
          const assignmentResult = await apiClient.query(orgUnitEmployeesByOrgUnit, {
            orgUnitId: unit.id,
            filter: {
              clientId: { eq: clientId }
            },
            limit: 10  // Get more assignments to find the most recent
          });
          const assignments = assignmentResult?.data?.orgUnitEmployeesByOrgUnit?.items || [];
          
          // Get the most recent assignment (active or inactive)
          let assignment = null;
          if (assignments.length > 0) {
            // Sort by effectiveDate or createdAt to get the most recent
            assignments.sort((a, b) => {
              const dateA = new Date(a.effectiveDate || a.createdAt);
              const dateB = new Date(b.effectiveDate || b.createdAt);
              return dateB - dateA; // Most recent first
            });
            assignment = assignments[0];
          }
          if (assignment) {
            managersByUnit[unit.id] = {
              managerId: assignment.employeeId,
              managerName: assignment.employeeNameSnapshot || assignment.employeeNameSnapshot,
              isActive: assignment.isActive
            };
          }
        } catch (error) {
          // No manager found, that's ok
        }
      });
      
      await Promise.all(managerPromises);

      // Return as array with additional metadata including manager info
      const inactiveUnitsWithMetadata = inactiveUnits.map(unit => ({
        id: unit.id,
        name: unit.name,
        orgLevel: unit.orgLevel,
        isActive: unit.isActive,
        parentOrgUnitId: unit.parentOrgUnitId,
        deactivatedDate: unit.updatedAt, // Use updatedAt as deactivation date
        managerId: managersByUnit[unit.id]?.managerId || unit.managerId || unit.managerUserId,
        managerName: managersByUnit[unit.id]?.managerName || null,
        managerUserId: unit.managerUserId
      }));
      
      return inactiveUnitsWithMetadata;
    } catch (error) {
      console.error('❌ Error fetching inactive org units history:', error);
      return [];
    }
  }

  // Helper method to get level type label
  getLevelTypeLabel(orgLevel) {
    // Note: Higher number = higher organizational level
    // ORGLEVEL_3 = Region (highest)
    // ORGLEVEL_2 = Area/District
    // ORGLEVEL_1 = Market/Territory (lowest org level above stores)
    const levelMap = {
      'ORGLEVEL_3': 'Region',
      'ORGLEVEL_2': 'Area',
      'ORGLEVEL_1': 'District',
      'BRAND': 'Brand',
      'STORE_LEVEL': 'Store'
    };
    return levelMap[orgLevel] || orgLevel;
  }

  // Helper method to get org level number
  getOrgLevelNumber(orgLevel) {
    const match = orgLevel?.match(/ORGLEVEL_(\d)/);
    return match ? parseInt(match[1]) : 0;
  }
}

export default new OrgUnitService();